import { HttpAgent } from '@dfinity/agent'
import { Value } from '../../idls/tokens/icrc1.did'
import getTokenActor from '../../utils/tokenActions/getTokenActor'

import type { TokenDetails } from '../../interfaces/common'

const parseMetadata = (metadata: [string, Value][]) => {
  const normalizedMeta = Object.fromEntries(metadata.map(([k, v]) => [k.replace('icrc1:', ''), v]))
  const result: Pick<TokenDetails, 'fee' | 'name' | 'symbol' | 'decimals' | 'logo'> = {
    fee: 0,
    name: '',
    symbol: '',
    decimals: 8,
    logo: '',
  }

  if (normalizedMeta.fee && 'Nat' in normalizedMeta.fee) {
    result.fee = Number(normalizedMeta.fee.Nat)
  }

  if (normalizedMeta.name && 'Text' in normalizedMeta.name) {
    result.name = normalizedMeta.name.Text
  }

  if (normalizedMeta.symbol && 'Text' in normalizedMeta.symbol) {
    result.symbol = normalizedMeta.symbol.Text
  }

  if (normalizedMeta.decimals && 'Nat' in normalizedMeta.decimals) {
    result.decimals = Number(normalizedMeta.decimals.Nat)
  }

  if (normalizedMeta.logo && 'Text' in normalizedMeta.logo) {
    result.logo = normalizedMeta.logo.Text
  }

  return result
}

const getTokenMetadata = async (
  canisterId: string,
  standard: string,
  agent: HttpAgent
): Promise<TokenDetails> => {
  const actor = getTokenActor(canisterId, standard, agent)

  const icrcActor = actor.icp || actor.icrc1 || actor.icrc2
  if (icrcActor) {
    const metadata = await icrcActor.icrc1_metadata()
    return { principalId: canisterId, standard, ...parseMetadata(metadata) }
  }

  if (actor.dip20) {
    const {
      metadata: { fee, name, logo, symbol, decimals },
    } = await actor.dip20.getTokenInfo()

    return {
      name,
      symbol,
      decimals: Number(decimals),
      fee: Number(fee),
      logo,
      principalId: canisterId,
      standard,
    }
  }

  if (actor.drc20) {
    const name = await actor.drc20.drc20_name()
    const symbol = await actor.drc20.drc20_symbol()
    const decimals = await actor.drc20.drc20_decimals()
    const fee = await actor.drc20.drc20_fee()
    const metadata = await actor.drc20.drc20_metadata()

    const logo = metadata.find((md) => md.name === 'logo')?.content || ''

    return {
      name,
      symbol,
      decimals,
      fee: Number(fee),
      logo,
      principalId: canisterId,
      standard,
    }
  }

  if (actor.xtc) {
    const meta = await actor.xtc.getMetadata()

    return {
      name: meta.name,
      symbol: meta.symbol,
      decimals: meta.decimals,
      fee: Number(meta.fee),
      logo: meta.logo,
      principalId: canisterId,
      standard: 'XTC',
    }
  }

  return Promise.reject('No token actor')
}

export default getTokenMetadata
